
import { useState, useRef, useEffect } from 'react';

interface AuditService {
  title: string;
  description: string;
  icon: string;
  deliverables: string[];
  duration: string;
  methodology: string[];
}

export default function SecurityAudits() {
  const [visibleItems, setVisibleItems] = useState<number[]>([]);
  const [selectedService, setSelectedService] = useState<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  const auditServices: AuditService[] = [
    {
      title: "Vulnerability Assessment",
      description: "Comprehensive scanning and identification of security vulnerabilities across your infrastructure",
      icon: "ri-search-eye-line",
      deliverables: ["Vulnerability Report", "Risk Assessment", "Remediation Plan", "Executive Summary"],
      duration: "2-4 weeks",
      methodology: ["Automated Scanning", "Manual Testing", "Configuration Review", "Network Analysis"]
    },
    {
      title: "Penetration Testing",
      description: "Ethical hacking to identify exploitable vulnerabilities and security weaknesses",
      icon: "ri-hammer-line",
      deliverables: ["Penetration Test Report", "Proof of Concepts", "Risk Ratings", "Remediation Guide"],
      duration: "3-6 weeks",
      methodology: ["External Testing", "Internal Testing", "Web App Testing", "Social Engineering"]
    },
    {
      title: "Compliance Audits",
      description: "Evaluate adherence to regulatory standards and industry compliance requirements",
      icon: "ri-file-shield-line",
      deliverables: ["Compliance Report", "Gap Analysis", "Control Assessment", "Certification Support"],
      duration: "4-8 weeks",
      methodology: ["Document Review", "Control Testing", "Interview Process", "Evidence Collection"]
    },
    {
      title: "Security Architecture Review",
      description: "Comprehensive evaluation of security design and implementation across systems",
      icon: "ri-building-line",
      deliverables: ["Architecture Assessment", "Design Recommendations", "Security Blueprint", "Implementation Roadmap"],
      duration: "6-10 weeks",
      methodology: ["Architecture Analysis", "Threat Modeling", "Control Evaluation", "Best Practice Review"]
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.getAttribute('data-index') || '0');
            setVisibleItems(prev => [...prev, index]);
          }
        });
      },
      { threshold: 0.2 }
    );

    const items = sectionRef.current?.querySelectorAll('.audit-item');
    items?.forEach(item => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 px-4 sm:px-6 lg:px-8 relative">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            <span className="bg-gradient-to-r from-red-400 to-orange-400 bg-clip-text text-transparent">
              Security Audits & Penetration Testing
            </span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Professional security assessments to identify vulnerabilities and strengthen your security posture
          </p>
        </div>

        {/* Hero Visualization */}
        <div className="mb-16 text-center">
          <div className="relative inline-block group">
            <img 
              src="https://readdy.ai/api/search-image?query=3D%20rendered%20cybersecurity%20audit%20and%20penetration%20testing%20visualization%20showing%20security%20analysis%20dashboard%2C%20vulnerability%20scanning%20process%2C%20ethical%20hacking%20tools%20interface%2C%20security%20assessment%20workflow%2C%20red%20team%20testing%20environment%20with%20multiple%20screens%20and%20security%20analysis%20charts&width=1200&height=600&seq=audit3d&orientation=landscape"
              alt="3D Security Audit Visualization"
              className="max-w-full h-auto rounded-2xl shadow-2xl hover:scale-105 transform transition-all duration-700 cursor-pointer"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-red-600/20 to-orange-600/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          </div>
        </div>

        {/* Audit Services Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {auditServices.map((service, index) => (
            <div
              key={index}
              data-index={index}
              className={`audit-item group bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 hover:bg-slate-800/60 transform transition-all duration-700 hover:scale-102 cursor-pointer ${
                visibleItems.includes(index) ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              } ${selectedService === index ? 'border-red-400 bg-slate-800/80' : ''}`}
              style={{ transitionDelay: `${index * 150}ms` }}
              onClick={() => setSelectedService(selectedService === index ? null : index)}
            >
              {/* Gradient border on hover */}
              <div className={`absolute inset-0 bg-gradient-to-r from-red-600 to-orange-600 opacity-0 group-hover:opacity-100 rounded-2xl transition-opacity duration-300 blur-sm ${
                selectedService === index ? 'opacity-50' : ''
              }`}></div>
              
              <div className="relative z-10 bg-slate-800/90 rounded-2xl p-8 -m-2">
                {/* Header */}
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center">
                    <div className={`w-16 h-16 bg-gradient-to-r from-red-600 to-orange-600 rounded-xl flex items-center justify-center mr-4 group-hover:animate-pulse ${
                      selectedService === index ? 'animate-pulse' : ''
                    }`}>
                      <i className={`${service.icon} text-2xl text-white`}></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-1 group-hover:text-red-300 transition-colors duration-300">
                        {service.title}
                      </h3>
                      <span className="text-sm text-orange-400 font-semibold">{service.duration}</span>
                    </div>
                  </div>
                  
                  <i className={`ri-${selectedService === index ? 'subtract' : 'add'}-line text-red-400 text-xl transition-transform duration-300 ${
                    selectedService === index ? 'rotate-180' : ''
                  }`}></i>
                </div>

                <p className="text-gray-400 mb-6 leading-relaxed">
                  {service.description}
                </p>

                {/* Expanded content */}
                {selectedService === index && (
                  <div className="space-y-6 mt-6 pt-6 border-t border-slate-700/50">
                    {/* Methodology */}
                    <div>
                      <h4 className="font-semibold text-white mb-3 flex items-center">
                        <i className="ri-settings-line text-red-400 mr-2"></i>
                        Methodology
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {service.methodology.map((method, methodIndex) => (
                          <div key={methodIndex} className="flex items-center text-sm text-gray-300 p-2 bg-slate-700/30 rounded-lg">
                            <i className="ri-arrow-right-s-line text-red-400 mr-2"></i>
                            {method}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Deliverables */}
                    <div>
                      <h4 className="font-semibold text-white mb-3 flex items-center">
                        <i className="ri-file-list-line text-orange-400 mr-2"></i>
                        Deliverables
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {service.deliverables.map((deliverable, deliverableIndex) => (
                          <div key={deliverableIndex} className="flex items-center text-sm text-gray-300 p-2 bg-slate-700/30 rounded-lg">
                            <i className="ri-check-line text-green-400 mr-2"></i>
                            {deliverable}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Security Testing Process */}
        <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 lg:p-12 mb-16">
          <h3 className="text-2xl sm:text-3xl font-bold text-center text-white mb-12">
            <span className="bg-gradient-to-r from-red-400 to-orange-400 bg-clip-text text-transparent">
              Security Testing Process
            </span>
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
            {[
              { step: "1", title: "Planning", desc: "Define scope and objectives", icon: "ri-file-text-line" },
              { step: "2", title: "Reconnaissance", desc: "Gather target information", icon: "ri-search-line" },
              { step: "3", title: "Scanning", desc: "Identify vulnerabilities", icon: "ri-radar-line" },
              { step: "4", title: "Exploitation", desc: "Test identified weaknesses", icon: "ri-hammer-line" },
              { step: "5", title: "Analysis", desc: "Assess impact and risk", icon: "ri-line-chart-line" },
              { step: "6", title: "Reporting", desc: "Document findings", icon: "ri-file-paper-line" }
            ].map((phase, index) => (
              <div
                key={index}
                data-index={index + 10}
                className={`audit-item text-center group relative ${
                  visibleItems.includes(index + 10) ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                } transition-all duration-500`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="relative mb-4">
                  <div className={`w-16 h-16 mx-auto bg-gradient-to-r from-red-600 to-orange-600 rounded-full flex items-center justify-center group-hover:animate-pulse shadow-lg`}>
                    <i className={`${phase.icon} text-xl text-white`}></i>
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                    {phase.step}
                  </div>
                </div>
                
                <h4 className="font-semibold text-white mb-2 group-hover:text-red-300 transition-colors duration-300">
                  {phase.title}
                </h4>
                <p className="text-gray-400 text-sm leading-relaxed">{phase.desc}</p>

                {/* Connection line */}
                {index < 5 && (
                  <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-red-500/50 to-orange-500/50 z-0"></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Audit Benefits */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16">
          {[
            { benefit: "Risk Reduction", description: "Identify and mitigate security risks", icon: "ri-shield-check-line" },
            { benefit: "Compliance", description: "Meet regulatory requirements", icon: "ri-file-shield-line" },
            { benefit: "Security Posture", description: "Strengthen overall security", icon: "ri-lock-line" },
            { benefit: "Cost Savings", description: "Prevent costly breaches", icon: "ri-money-dollar-circle-line" }
          ].map((benefit, index) => (
            <div
              key={index}
              data-index={index + 20}
              className={`audit-item text-center p-6 bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl hover:bg-slate-800/60 transform transition-all duration-500 hover:scale-105 cursor-pointer ${
                visibleItems.includes(index + 20) ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}
              style={{ transitionDelay: `${(index + 15) * 100}ms` }}
            >
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-red-600 to-orange-600 rounded-xl flex items-center justify-center">
                <i className={`${benefit.icon} text-2xl text-white`}></i>
              </div>
              <h4 className="font-bold text-white mb-2">{benefit.benefit}</h4>
              <p className="text-gray-400 text-sm">{benefit.description}</p>
            </div>
          ))}
        </div>

        {/* Final CTA Section */}
        <div className="text-center bg-gradient-to-r from-slate-800/60 to-slate-900/60 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-12">
          <h3 className="text-2xl sm:text-3xl font-bold text-white mb-6">
            Ready to Strengthen Your Security?
          </h3>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed">
            Get started with a comprehensive security assessment to identify vulnerabilities and improve your security posture.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <button className="group px-8 py-4 bg-gradient-to-r from-red-600 to-orange-600 text-white font-semibold rounded-full hover:from-red-700 hover:to-orange-700 transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-2xl">
              <span className="relative z-10">Schedule Security Audit</span>
              <div className="absolute inset-0 bg-gradient-to-r from-red-400 to-orange-400 rounded-full opacity-0 group-hover:opacity-50 transition-opacity duration-300"></div>
            </button>
            
            <button className="group px-8 py-4 border-2 border-orange-400 text-orange-400 font-semibold rounded-full hover:bg-orange-400 hover:text-slate-900 transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer">
              Request Quote
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
